/** Now Dashboard — Timeline tab: merged 24h/7d activity feed with kind/person
 *  filters and a "Follow user" pinned mode (localStorage-backed). */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  LogIn, LogOut, Plane, Home, ListChecks, CheckCircle2, ShieldAlert,
  Trophy, RefreshCw, AlertTriangle, Pin, PinOff, X,
} from "lucide-react";
import { EmployeeName } from "@/lib/employeeProfile";

const KIND_META = {
  clock_in: { icon: LogIn, cls: "text-emerald-500", label: "Checked in" },
  clock_out: { icon: LogOut, cls: "text-sky-500", label: "Checked out" },
  leave_request: { icon: Plane, cls: "text-red-500", label: "Leave request" },
  wfh_request: { icon: Home, cls: "text-violet-500", label: "WFH request" },
  task_created: { icon: ListChecks, cls: "text-blue-500", label: "Task created" },
  task_done: { icon: CheckCircle2, cls: "text-emerald-500", label: "Task done" },
  anomaly_review: { icon: ShieldAlert, cls: "text-orange-500", label: "Anomaly reviewed" },
  points: { icon: Trophy, cls: "text-amber-500", label: "Points" },
};

const ALL_KINDS = Object.keys(KIND_META);
const FOLLOW_KEY = "now_follow_user_v1";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.round((now - d) / 60000);
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / (60 * 24))}d ago`;
  } catch { return iso.slice(11, 16); }
}

export default function NowTimelineTab() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [window, setWindow] = useState("24h");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedKinds, setSelectedKinds] = useState(new Set(ALL_KINDS));
  const [personFilter, setPersonFilter] = useState("");   // user_id
  const [members, setMembers] = useState([]);
  const [follow, setFollow] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FOLLOW_KEY) || "null"); } catch { return null; }
  });

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
  }, [wsId]);

  const followedUid = follow?.user_id || null;
  const activePerson = followedUid || personFilter;

  const buildUrl = () => {
    const p = new URLSearchParams({ window, limit: "200" });
    if (activePerson) p.set("user_id", activePerson);
    if (selectedKinds.size !== ALL_KINDS.length && selectedKinds.size > 0) {
      p.set("kinds", Array.from(selectedKinds).join(","));
    }
    return `/workspaces/${wsId}/now/timeline?${p.toString()}`;
  };

  const load = () => {
    if (!wsId) return;
    setBusy(true);
    api.get(buildUrl())
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [wsId, window, personFilter, followedUid, Array.from(selectedKinds).sort().join(",")]);// eslint-disable-line

  // Auto-refresh in follow mode (every 20s)
  useEffect(() => {
    if (!followedUid) return;
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [followedUid, window]);// eslint-disable-line

  const toggleKind = (k) => {
    const s = new Set(selectedKinds);
    if (s.has(k)) s.delete(k); else s.add(k);
    if (s.size === 0) return; // don't allow zero
    setSelectedKinds(s);
  };
  const allKinds = () => setSelectedKinds(new Set(ALL_KINDS));

  const startFollow = (uid, name) => {
    const rec = uid ? { user_id: uid, name } : null;
    setFollow(rec);
    if (rec) localStorage.setItem(FOLLOW_KEY, JSON.stringify(rec));
    else localStorage.removeItem(FOLLOW_KEY);
    setPersonFilter("");
  };

  const personMap = useMemo(() => {
    const m = new Map();
    for (const mem of members) if (mem.user_id) m.set(mem.user_id, mem.user?.name || mem.user?.email || mem.user_id);
    return m;
  }, [members]);

  return (
    <div className="space-y-3" data-testid="now-timeline-tab">
      {follow && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-3 flex items-center gap-2" data-testid="timeline-follow-banner">
          <Pin className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-xs">Following <b>{follow.name}</b> — auto-refreshes every 20s.</span>
          <Button size="sm" variant="ghost" className="rounded-none h-6 ml-auto" onClick={() => startFollow(null)} data-testid="timeline-unfollow">
            <PinOff className="w-3.5 h-3.5 mr-1" />Unfollow
          </Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {["24h", "7d"].map((w) => (
            <Button
              key={w} size="sm" variant={window === w ? "default" : "outline"}
              className="rounded-none h-7 text-xs" onClick={() => setWindow(w)}
              data-testid={`timeline-window-${w}`}
            >
              {w === "24h" ? "Last 24 hours" : "Last 7 days"}
            </Button>
          ))}
        </div>
        {!follow && (
          <Select value={personFilter || "__all__"} onValueChange={(v) => setPersonFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-7 w-56 rounded-none text-xs" data-testid="timeline-person-filter"><SelectValue placeholder="Filter by person…" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="__all__">All people</SelectItem>
              {members.filter((m) => m.user_id).map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name || m.user?.email || m.user_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {personFilter && !follow && (
          <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => startFollow(personFilter, personMap.get(personFilter) || "member")} data-testid="timeline-follow-btn">
            <Pin className="w-3.5 h-3.5 mr-1" />Follow
          </Button>
        )}
        <Button size="sm" variant="ghost" className="rounded-none h-7" onClick={load} disabled={busy} data-testid="timeline-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
        <span className="ml-auto text-[11px] text-muted-foreground">{data?.count || 0} events</span>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="timeline-kind-chips">
        <button type="button" onClick={allKinds} className="text-[10px] px-2 py-0.5 border rounded-none hover:bg-muted" data-testid="timeline-kind-all">All</button>
        {ALL_KINDS.map((k) => {
          const meta = KIND_META[k];
          const on = selectedKinds.has(k);
          const Icon = meta.icon;
          return (
            <button
              key={k} type="button" onClick={() => toggleKind(k)}
              className={`text-[10px] inline-flex items-center gap-1 px-2 py-0.5 border rounded-none transition ${on ? "bg-foreground text-background" : "hover:bg-muted opacity-60"}`}
              data-testid={`timeline-kind-${k}`}
            >
              <Icon className={`w-3 h-3 ${on ? "" : meta.cls}`} />
              {meta.label}
            </button>
          );
        })}
      </div>
      <div className="border" data-testid="now-timeline-list">
        {!data && <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>}
        {data && data.events.length === 0 && (
          <div className="p-8 text-sm text-muted-foreground text-center">No activity matches these filters.</div>
        )}
        {(data?.events || []).map((e, i) => (
          <TimelineEvent key={`${e.kind}-${e.at}-${i}`} e={e} onFollow={startFollow} following={follow?.user_id === e.user_id} />
        ))}
      </div>
    </div>
  );
}

function TimelineEvent({ e, onFollow, following }) {
  const m = KIND_META[e.kind] || { icon: AlertTriangle, cls: "text-muted-foreground", label: e.kind };
  const Icon = m.icon;
  return (
    <div className="border-t px-3 py-2 flex items-start gap-3 text-xs group" data-testid={`timeline-event-${e.kind}`}>
      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${m.cls}`} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-1.5">
          {e.person && <EmployeeName userId={e.person.user_id} name={e.person.name} className="font-semibold" />}
          <span className="text-muted-foreground">{m.label.toLowerCase()}</span>
          <EventMeta e={e} />
        </div>
        {e.person?.department && <div className="text-[10px] text-muted-foreground">{e.person.department}</div>}
      </div>
      {e.person && !following && (
        <button
          type="button" onClick={() => onFollow(e.person.user_id, e.person.name)}
          className="opacity-0 group-hover:opacity-100 transition text-[10px] text-primary hover:underline shrink-0"
          data-testid={`timeline-event-follow-${e.person.user_id}`}
          title="Follow this person"
        ><Pin className="w-3 h-3" /></button>
      )}
      <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">{fmtTime(e.at)}</span>
    </div>
  );
}

function EventMeta({ e }) {
  const m = e.meta || {};
  if (e.kind === "clock_in") {
    return (
      <>
        {m.late > 0 && <Badge variant="outline" className="rounded-none border-red-500/40 text-red-500 text-[9px]">+{m.late}m late</Badge>}
        {(m.anomalies || []).length > 0 && <Badge variant="outline" className="rounded-none border-orange-500/40 text-orange-600 text-[9px]">anomaly</Badge>}
        {m.location && <span className="text-[10px] text-muted-foreground">· {String(m.location).slice(0, 32)}</span>}
      </>
    );
  }
  if (e.kind === "leave_request") {
    return <span className="text-[10px]"><b>{m.days}d</b> {m.type} · {m.from}{m.to !== m.from ? `→${m.to}` : ""} <span className={m.status === "approved" ? "text-emerald-600" : m.status === "rejected" ? "text-red-500" : "text-amber-500"}>({m.status})</span></span>;
  }
  if (e.kind === "wfh_request") {
    return <span className="text-[10px]">{m.date} <span className={m.status === "approved" ? "text-emerald-600" : m.status === "rejected" ? "text-red-500" : "text-amber-500"}>({m.status})</span></span>;
  }
  if (e.kind === "task_created" || e.kind === "task_done") {
    return <span className="text-[10px] truncate max-w-96">&ldquo;{m.title}&rdquo;</span>;
  }
  if (e.kind === "anomaly_review") {
    return <span className="text-[10px]"><b>{m.action}</b> by <i>{m.by}</i>{m.note ? ` — ${m.note}` : ""}</span>;
  }
  if (e.kind === "points") {
    return <span className="text-[10px]"><b className={m.points >= 0 ? "text-emerald-600" : "text-red-500"}>{m.points > 0 ? `+${m.points}` : m.points}</b> · {m.reason}</span>;
  }
  return null;
}
